// src/Dashboard/pages/user/News/components/NewsDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaEye,
  FaEyeSlash,
  FaImage,
  FaSave,
  FaTimes,
  FaTrash,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  deleteNews,
  updateNews,
} from "../../../../../services/newsServices";

import {
  toAbsolutePhotoUrl,
  uploadNewsPhoto,
} from "../../../../../services/newsPhotoServices";

import {
  FALLBACK_NEWS_IMAGE,
  normalizePlainText,
  normalizeRichText,
  onImgError,
  stripHtml,
  withBust,
} from "../utils/newsDashboardUtils";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "clean",
];

const emptyForm = {
  id: "",
  title: "",
  content: "",
  image_url: "",
  author_id: "",
  is_active: true,
};

export default function NewsDrawer({
  isOpen,
  mode = "create",
  news,
  user,
  onClose,
  onSaved,
}) {
  const isCreate = mode === "create";
  const isEdit = mode === "edit";

  const titleRef = useRef(null);
  const fileInputRef = useRef(null);

  const initial = useMemo(() => {
    return {
      id: news?.id ?? "",
      title: news?.title ?? "",
      content: news?.content ?? "",
      image_url: news?.image_url ?? "",
      author_id: news?.author_id ?? user?.userId ?? "",
      is_active: news?.is_active === undefined ? true : !!news?.is_active,
    };
  }, [news, user?.userId]);

  const [formData, setFormData] = useState(initial);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [imgVersion, setImgVersion] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initial);
    setSelectedFile(null);
    setSaving(false);
    setDeleting(false);
    setUploading(false);
    setUploadProgress(0);
    setImgVersion((value) => value + 1);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const timer = window.setTimeout(() => {
      titleRef.current?.focus();
    }, 80);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initial]);

  const isDirty =
    formData.title !== initial.title ||
    formData.content !== initial.content ||
    formData.image_url !== initial.image_url ||
    formData.is_active !== initial.is_active ||
    !!selectedFile;

  const descChars = stripHtml(formData.content).length;

  const coverAbs = toAbsolutePhotoUrl(
    formData.image_url?.trim() ? formData.image_url : FALLBACK_NEWS_IMAGE
  );

  const cover = withBust(coverAbs, imgVersion);

  if (!isOpen) return null;

  const handleClose = () => {
    if (saving || uploading || deleting) return;

    if (
      !isDirty ||
      window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")
    ) {
      onClose?.();
    }
  };

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFile = (file) => {
    if (!file) return;

    const validType = /^image\/(png|jpe?g|webp)$/i.test(file.type);

    if (!validType) {
      toast.error("Format d’image non supporté. Utilisez PNG, JPG ou WEBP.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux. Taille maximale : 10 Mo.");
      return;
    }

    setSelectedFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    handleFile(event.dataTransfer.files?.[0] || null);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleUpload = async () => {
    if (!formData.id) {
      toast.error("Aucun brouillon d’actualité n’est disponible.");
      return;
    }

    if (!selectedFile) {
      toast.warn("Sélectionnez une image.");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadNewsPhoto(formData.id, selectedFile, {
        format: outputFormat,
        onUploadProgress: (event) => {
          if (!event.total) return;

          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        },
      });

      setFormData((previous) => ({
        ...previous,
        image_url: updated?.image_url || previous.image_url,
      }));

      setImgVersion((value) => value + 1);
      setSelectedFile(null);
      setUploadProgress(0);

      toast.success("Image téléversée et optimisée.");
    } catch (error) {
      console.error("Erreur upload image actualité :", error);
      toast.error("Erreur lors du téléversement de l’image.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const title = normalizePlainText(formData.title);

    if (!formData.id) {
      toast.error("Identifiant de l’actualité manquant.");
      return;
    }

    if (!title) {
      toast.warn("Le titre est obligatoire.");
      titleRef.current?.focus();
      return;
    }

    try {
      setSaving(true);

      await updateNews({
        id: formData.id,
        title,
        content: normalizeRichText(formData.content),
        image_url: formData.image_url,
        author_id: formData.author_id || user?.userId,
        is_active: formData.is_active ? 1 : 0,
      });

      toast.success(isCreate ? "Actualité créée." : "Actualité mise à jour.");
      await onSaved?.();
    } catch (error) {
      console.error("Erreur enregistrement actualité :", error);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.id) return;

    const ok = window.confirm("Supprimer définitivement cette actualité ?");

    if (!ok) return;

    try {
      setDeleting(true);

      await deleteNews(formData.id);

      toast.success("Actualité supprimée.");
      await onSaved?.();
    } catch (error) {
      console.error("Erreur suppression actualité :", error);
      toast.error("Erreur lors de la suppression.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              <FaImage />
              Actualité
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {isEdit ? "Modifier l’actualité" : "Créer une actualité"}
            </h2>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
              <div className="space-y-4">
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="overflow-hidden rounded-[24px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950"
                >
                  <div
                    className="relative w-full bg-white dark:bg-slate-900"
                    style={{ aspectRatio: "16 / 9" }}
                  >
                    <img
                      src={cover}
                      alt="Illustration actualité"
                      className="h-full w-full object-contain"
                      onError={onImgError}
                    />
                  </div>

                  <div className="space-y-3 p-4">
                    <div className="text-sm text-slate-600 dark:text-slate-300">
                      Déposez une image ou{" "}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="font-bold text-[#013E8E] underline dark:text-indigo-300"
                      >
                        sélectionnez un fichier
                      </button>
                      .
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) =>
                        handleFile(event.target.files?.[0] || null)
                      }
                    />

                    {selectedFile && (
                      <div className="rounded-2xl bg-white px-3 py-2 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                        {selectedFile.name}
                      </div>
                    )}

                    <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                      <select
                        value={outputFormat}
                        onChange={(event) => setOutputFormat(event.target.value)}
                        disabled={uploading}
                        className="rounded-2xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      >
                        <option value="webp">WEBP recommandé</option>
                        <option value="jpg">JPG</option>
                        <option value="png">PNG</option>
                      </select>

                      <button
                        type="button"
                        onClick={handleUpload}
                        disabled={uploading || !selectedFile || !formData.id}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <FaUpload />
                        {uploading ? "Transfert..." : "Téléverser"}
                      </button>
                    </div>

                    {uploading && (
                      <div>
                        <progress
                          className="w-full"
                          value={uploadProgress}
                          max="100"
                        />
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {uploadProgress}%
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    URL image serveur
                  </label>

                  <input
                    type="text"
                    name="image_url"
                    value={formData.image_url}
                    readOnly
                    placeholder="Remplie automatiquement après upload"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                  <label className="mb-3 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Visibilité
                  </label>

                  <button
                    type="button"
                    onClick={() =>
                      setFormData((previous) => ({
                        ...previous,
                        is_active: !previous.is_active,
                      }))
                    }
                    className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold ring-1 transition ${
                      formData.is_active
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                        : "bg-slate-200 text-slate-700 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700"
                    }`}
                  >
                    {formData.is_active ? <FaEye /> : <FaEyeSlash />}
                    {formData.is_active
                      ? "Actualité visible"
                      : "Actualité masquée"}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Titre *
                  </label>

                  <input
                    ref={titleRef}
                    type="text"
                    name="title"
                    maxLength={160}
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Ex. Résultats tournoi régional"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {formData.title.length}/160 caractères
                  </p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Contenu
                  </label>

                  <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950">
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={(value) =>
                        setFormData((previous) => ({
                          ...previous,
                          content: value,
                        }))
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Rédigez l’actualité..."
                    />
                  </div>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {descChars} caractère{descChars > 1 ? "s" : ""} en texte
                    brut
                  </p>
                </div>
              </div>
            </div>
          </div>

          <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              {formData.id && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting || saving || uploading}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaTrash />
                  {deleting ? "Suppression..." : "Supprimer"}
                </button>
              )}

              <button
                type="button"
                onClick={handleClose}
                disabled={saving || uploading || deleting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={
                  saving ||
                  uploading ||
                  deleting ||
                  !normalizePlainText(formData.title)
                }
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </footer>
        </form>
      </aside>
    </div>
  );
}